<template>
  <div class="custom-attributes--panel">
    <table v-if="filteredAttributes.length" class="attributes-table">
      <thead>
        <tr>
          <th class="attribute-header">
            {{ $t('CUSTOM_ATTRIBUTES.TABLE.ATTRIBUTE_NAME') }}
          </th>
          <th class="value-header">
            {{ $t('CUSTOM_ATTRIBUTES.TABLE.VALUE') }}
          </th>
          <th class="action-header">
            {{ $t('CUSTOM_ATTRIBUTES.TABLE.ACTION') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="attribute in filteredAttributes"
          :key="attribute.id"
          class="attribute-row"
        >
          <!-- Attribute Name Column -->
          <td class="attribute-name-cell">
            <span class="attribute-name">
              {{ attribute.attribute_display_name }}
            </span>
          </td>

          <!-- Value Column -->
          <td class="value-cell">
            <!-- Editing Mode -->
            <div
              v-if="
                editingKey === attribute.attribute_key &&
                attribute.attribute_display_type !== 'checkbox' &&
                attribute.attribute_display_type !== 'list'
              "
              class="edit-input-container"
            >
              <div class="input-group small">
                <input
                  ref="inputfield"
                  v-model="editedValues[attribute.attribute_key]"
                  :type="getInputType(attribute.attribute_display_type)"
                  class="input-group-field"
                  autofocus
                  @keyup.enter="
                    onSaveEdit(
                      attribute.attribute_key,
                      attribute.attribute_display_type
                    )
                  "
                  @keyup.escape="onCancelEdit"
                />
                <div class="input-group-button">
                  <woot-button
                    size="small"
                    icon="checkmark"
                    @click="
                      onSaveEdit(
                        attribute.attribute_key,
                        attribute.attribute_display_type
                      )
                    "
                  />
                  <woot-button
                    size="small"
                    variant="smooth"
                    color-scheme="secondary"
                    icon="dismiss"
                    class-names="cancel-button"
                    @click="onCancelEdit"
                  />
                </div>
              </div>
            </div>

            <!-- List Type -->
            <div
              v-else-if="attribute.attribute_display_type === 'list'"
              class="list-dropdown"
            >
              <multiselect-dropdown
                :options="getListOptions(attribute.attribute_values)"
                :selected-item="
                  getSelectedItem(
                    attribute.attribute_values,
                    attribute.attribute_key
                  )
                "
                :has-thumbnail="false"
                :multiselector-placeholder="
                  $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
                "
                :no-search-result="
                  $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
                "
                :input-placeholder="
                  $t(
                    'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
                  )
                "
                @click="value => onUpdateListValue(attribute.attribute_key, value)"
              />
            </div>

            <!-- Checkbox Type - Edit Mode -->
            <div
              v-else-if="attribute.attribute_display_type === 'checkbox' && editingKey === attribute.attribute_key"
              class="checkbox-edit-container"
            >
              <div class="flex items-center gap-2">
                <input
                  v-model="editedValues[attribute.attribute_key]"
                  type="checkbox"
                  class="checkbox"
                />
                <span class="text-sm text-slate-600 dark:text-slate-300">
                  {{ editedValues[attribute.attribute_key] ? 'Yes' : 'No' }}
                </span>
                <woot-button
                  size="small"
                  icon="checkmark"
                  @click="onCheckboxUpdate(attribute.attribute_key)"
                />
                <woot-button
                  size="small"
                  variant="smooth"
                  color-scheme="secondary"
                  icon="dismiss"
                  class-names="cancel-button"
                  @click="onCancelEdit"
                />
              </div>
            </div>

            <!-- Checkbox Type - Value display -->
            <div
              v-else-if="attribute.attribute_display_type === 'checkbox'"
              class="value-display"
            >
              <span class="value-text">
                {{ attribute.value ? 'Yes' : 'No' }}
              </span>
            </div>

            <!-- Link Type -->
            <div
              v-else-if="attribute.attribute_display_type === 'link'"
              class="value-display"
            >
              <a
                :href="attribute.value"
                target="_blank"
                rel="noopener noreferrer"
                class="value-link"
                v-tooltip="getTruncatedTooltip(attribute.value)"
              >
                {{ attribute.value || '---' }}
              </a>
            </div>

            <!-- Default Display Mode -->
            <div v-else class="value-display">
              <span
                class="value-text"
                v-tooltip="getTruncatedTooltip(getDisplayValue(attribute))"
              >
                {{ getDisplayValue(attribute) }}
              </span>
            </div>
          </td>

          <!-- Action Column -->
          <td class="action-cell">
            <div class="action-buttons">
              <woot-button
                v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
                variant="smooth"
                size="tiny"
                color-scheme="secondary"
                icon="clipboard"
                @click="onCopy(attribute.value)"
              />
              <woot-button
                v-if="attribute.attribute_display_type !== 'list'"
                v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
                variant="smooth"
                size="tiny"
                color-scheme="secondary"
                icon="edit"
                @click="onStartEdit(attribute)"
              />
              <woot-button
                v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
                variant="smooth"
                size="tiny"
                color-scheme="alert"
                icon="delete"
                @click="openDeleteModal(attribute.attribute_key)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="no-attributes">
      <p class="text-slate-500 dark:text-slate-400 text-sm py-4 text-center">
        {{ $t('CUSTOM_ATTRIBUTES.NO_RECORDS') }}
      </p>
    </div>
    <div class="delete-modal-wrapper">
      <woot-delete-modal
        v-if="showDeleteModal"
        :show.sync="showDeleteModal"
        :on-close="closeDeleteModal"
        :on-confirm="confirmDelete"
        :title="$t('CUSTOM_ATTRIBUTES.DELETE.TITLE')"
        :message="$t('CUSTOM_ATTRIBUTES.DELETE.MESSAGE')"
        :confirm-text="$t('CUSTOM_ATTRIBUTES.DELETE.CONFIRM')"
        :reject-text="$t('CUSTOM_ATTRIBUTES.DELETE.CANCEL')"
      />
    </div>
  </div>
</template>

<script>
import { parseISO } from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import attributeMixin from 'dashboard/mixins/attributeMixin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';

export default {
  components: {
    MultiselectDropdown,
  },
  mixins: [alertMixin, attributeMixin],
  props: {
    attributeType: {
      type: String,
      default: 'conversation_attribute',
    },
    attributeClass: {
      type: String,
      default: '',
    },
    contactId: { type: Number, default: null },
    mycontact: { type: Object, default: null },
  },
  data() {
    return {
      editingKey: null,
      editedValues: {},
      showDeleteModal: false,
      deleteKey: null,
    };
  },
  watch: {
    filteredAttributes: {
      immediate: true,
      handler(attributes) {
        attributes.forEach(attr => {
          if (!(attr.attribute_key in this.editedValues)) {
            this.$set(this.editedValues, attr.attribute_key, attr.value);
          }
        });
      },
    },
  },
  methods: {
    getInputType(attributeType) {
      if (attributeType === 'link') return 'url';
      if (attributeType === 'date') return 'date';
      if (attributeType === 'number') return 'number';
      return 'text';
    },
    getDisplayValue(attribute) {
      if (attribute.attribute_display_type === 'date' && attribute.value) {
        return new Date(attribute.value).toLocaleDateString();
      }
      return attribute.value || '---';
    },
    getListOptions(values) {
      return (values || []).map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    getSelectedItem(values, key) {
      const selectedValue = this.editedValues[key];
      const id = (values || []).indexOf(selectedValue) + 1;
      return { id, name: selectedValue };
    },
    onStartEdit(attribute) {
      this.editingKey = attribute.attribute_key;
      this.$set(this.editedValues, attribute.attribute_key, attribute.value);
      this.$nextTick(() => {
        if (this.$refs.inputfield && this.$refs.inputfield[0]) {
          this.$refs.inputfield[0].focus();
        }
      });
    },
    onCancelEdit() {
      this.editingKey = null;
    },
    onSaveEdit(key, attributeType) {
      let value = this.editedValues[key];
      if (attributeType === 'date') {
        value = parseISO(value);
      }
      this.editingKey = null;
      this.onUpdate(key, value);
    },
    onCheckboxUpdate(key) {
      this.editingKey = null;
      this.onUpdate(key, this.editedValues[key]);
    },
    onUpdateListValue(key, value) {
      if (value) {
        this.$set(this.editedValues, key, value.name);
        this.onUpdate(key, value.name);
      }
    },
    async onUpdate(key, value) {
      const updatedAttributes = { ...this.customAttributes, [key]: value };
      try {
        if (this.attributeType === 'conversation_attribute') {
          await this.$store.dispatch('updateCustomAttributes', {
            conversationId: this.conversationId,
            customAttributes: updatedAttributes,
          });
        } else {
          this.$store.dispatch('contacts/update', {
            id: this.contactId,
            custom_attributes: updatedAttributes,
            custom_attributes_order: this.customAttributesOrder,
          });
        }
        this.showAlert(this.$t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR');
        this.showAlert(errorMessage);
      }
    },
    openDeleteModal(key) {
      this.deleteKey = key;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteKey = null;
    },
    async confirmDelete() {
      if (!this.deleteKey) return;
      try {
        const { [this.deleteKey]: remove, ...updatedAttributes } = this.customAttributes;
        if (this.attributeType === 'conversation_attribute') {
          await this.$store.dispatch('updateCustomAttributes', {
            conversationId: this.conversationId,
            customAttributes: updatedAttributes,
          });
        } else {
          this.$store.dispatch('contacts/deleteCustomAttributes', {
            id: this.contactId,
            customAttributes: [this.deleteKey],
          });
        }

        this.showAlert(this.$t('CUSTOM_ATTRIBUTES.FORM.DELETE.SUCCESS'));
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('CUSTOM_ATTRIBUTES.FORM.DELETE.ERROR');
        this.showAlert(errorMessage);
      } finally {
        this.closeDeleteModal();
      }
    },
    async onCopy(attributeValue) {
      await copyTextToClipboard(attributeValue);
      this.showAlert(this.$t('CUSTOM_ATTRIBUTES.COPY_SUCCESSFUL'));
    },
    getTruncatedTooltip(value) {
      if (!value || String(value).length <= 20) return false;
      return String(value);
    },
  },
};
</script>
<style scoped lang="scss">
.custom-attributes--panel {
  @apply w-full px-2;
}

.attributes-table {
  @apply w-full border-collapse text-sm;
  table-layout: fixed;
  /* Allow selecting cell text; parent sidebar may use drag-handle-only dragging */
  user-select: text;
  -webkit-user-select: text;

  thead {
    @apply bg-slate-50 dark:bg-slate-800;

    th {
      @apply text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide border-b border-slate-200 dark:border-slate-700;
    }
  }

  .value-header {
    @apply text-center;
  }

  .action-header {
    @apply text-right pr-6;
  }

  tbody {
    tr {
      @apply border-b border-slate-100 dark:border-slate-700;

      &:hover {
        @apply bg-slate-25 dark:bg-slate-800/50;
      }

      &:last-child {
        @apply border-b-0;
      }
    }

    td {
      @apply py-2.5 px-3 align-middle;
    }
  }
}

.attribute-name-cell {
  @apply w-1/4;
}

.attribute-name {
  @apply text-slate-700 dark:text-slate-200 font-bold block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.value-cell {
  @apply w-2/5;
  vertical-align: middle;
  text-align: center;
}

.value-display {
  @apply block;
}

.value-text {
  @apply text-slate-600 dark:text-slate-300 block mx-auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.value-link {
  @apply text-woot-500 dark:text-woot-400 hover:underline block mx-auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.action-cell {
  @apply w-1/4;
}

.action-buttons {
  @apply flex items-center justify-end gap-1;
}

.edit-input-container {
  @apply w-full;

  .input-group {
    @apply m-0;
  }

  .input-group-button {
    @apply flex items-center gap-1;
  }
}

.list-dropdown {
  @apply w-full;

  :deep {
    .selector-wrap {
      @apply m-0;
    }
  }
}

.checkbox {
  @apply cursor-pointer m-0;
  vertical-align: middle;
}

.checkbox-edit-container {
  @apply w-full h-full flex items-center;

  > div {
    @apply flex items-center gap-2;
  }
}

.no-attributes {
  @apply border border-dashed border-slate-200 dark:border-slate-700 rounded-lg;
}

.cancel-button {
  @apply rounded-md;
}

.delete-modal-wrapper {
  :deep(.modal-mask) {
    background-color: transparent;
    backdrop-filter: none;
  }
  
  :deep(.modal-container) {
    max-width: 400px;
    width: 90%;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
}
</style>
